import { useState } from 'react';
import { useAuthStore } from '@/features/auth/store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  CalendarCheck,
  Clock,
  CheckCircle,
  XCircle,
  CircleNotch,
  ArrowRight,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  fetchConferences,
  cancelConference,
  FORMAT_LABELS,
  PURPOSE_LABELS,
  type Conference,
} from '@/lib/conferencesApi';
import { ConfirmSlotModal } from '@/components/conferences/ConfirmSlotModal';
import { getApiErrorMessage } from '@/lib/api';

const POLL_MS = 60_000;

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export function ConferencesAwaitingTray() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const isBarrister = ['silk','junior','pupil','door_tenant'].includes(user?.memberType ?? '');
  const basePath = isClerk ? '/clerk' : isBarrister ? '/barrister' : '/admin';
  const [confirmTarget, setConfirmTarget] = useState<Conference | null>(null);

  const conferencesQ = useQuery({
    queryKey: ['conferences', { status: 'awaiting' }],
    queryFn: () => fetchConferences({ status: 'awaiting', limit: 20 }),
    refetchInterval: POLL_MS,
  });

  const cancelMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => cancelConference(id, reason),
    onSuccess: () => {
      toast.success('Conference cancelled');
      qc.invalidateQueries({ queryKey: ['conferences'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['hearings'] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not cancel')),
  });

  const conferences = conferencesQ.data?.conferences ?? [];

  if (conferencesQ.isLoading) {
    return (
      <Card className="flex items-center gap-2 px-4 py-3 text-xs text-muted-foreground">
        <CircleNotch size={12} className="animate-spin" /> Loading conferences…
      </Card>
    );
  }

  if (conferences.length === 0) {
    return null; // Hide the section entirely when nothing's awaiting.
  }

  return (
    <>
      <Card className="overflow-hidden border-amber-200 dark:border-amber-500/30">
        <div className="flex items-center justify-between border-b border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <CalendarCheck size={14} weight="duotone" className="text-amber-700 dark:text-amber-300" />
            <span className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              Conferences awaiting scheduling
            </span>
            <span className="rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900 dark:text-amber-200">
              {conferences.length}
            </span>
          </div>
          <span className="text-xs text-amber-800 dark:text-amber-300">Pick a slot to confirm</span>
        </div>
        <ul className="divide-y divide-border">
          {conferences.map((c) => {
            const proposedCount = c.proposedDates?.length ?? 0;
            const earliest = c.proposedDates?.[0];
            return (
              <li key={c.id} className="flex flex-wrap items-start gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => navigate(`${basePath}/matters/${c.caseId}`)}
                      className="text-xs text-brand-600 dark:text-brand-400 hover:underline"
                    >
                      {c.case.caseNumber}
                    </button>
                    <span className="truncate text-sm font-medium">{c.case.caseName}</span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock size={10} weight="duotone" /> requested {timeAgo(c.createdAt)}
                    </span>
                    <span>{c.requestedBy.firstName} {c.requestedBy.lastName}</span>
                    <span>{PURPOSE_LABELS[c.purpose]} · {FORMAT_LABELS[c.format]} · {c.durationMinutes}m</span>
                    {c.layClientAttending && <span className="text-amber-700 dark:text-amber-300">Lay client attending</span>}
                  </div>
                  {proposedCount > 0 && earliest && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      <strong className="text-foreground">{proposedCount}</strong> slot{proposedCount === 1 ? '' : 's'} proposed
                      {' · '}
                      earliest{' '}
                      <span className="text-foreground">
                        {new Date(earliest).toLocaleString('en-GB', {
                          weekday: 'short',
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  )}
                  {c.notes && (
                    <p className="mt-1 line-clamp-2 text-xs italic text-muted-foreground">
                      "{c.notes}"
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    size="sm"
                    onClick={() => setConfirmTarget(c)}
                    leftIcon={<CheckCircle className="h-3.5 w-3.5" />}
                  >
                    Schedule
                  </Button>
                  <button
                    onClick={() => {
                      const reason = window.prompt('Reason for cancelling? (will be saved to the audit log)');
                      if (reason && reason.trim()) {
                        cancelMut.mutate({ id: c.id, reason: reason.trim() });
                      }
                    }}
                    disabled={cancelMut.isPending}
                    title="Cancel conference"
                    className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-red-50 dark:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <XCircle size={13} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
        {conferences.length >= 20 && (
          <button
            onClick={() => navigate(basePath === '/admin' ? '/admin/calendar' : `${basePath}/diary`)}
            className="flex w-full items-center justify-center gap-1 border-t border-border bg-muted/30 py-2 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground"
          >
            See all <ArrowRight size={11} weight="bold" />
          </button>
        )}
      </Card>

      {confirmTarget && (
        <ConfirmSlotModal
          conference={confirmTarget}
          onClose={() => setConfirmTarget(null)}
          onConfirmed={() => setConfirmTarget(null)}
        />
      )}
    </>
  );
}
